import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { motion, AnimatePresence, type Transition } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
// import { announce } from '@app/shared/components/accessibility/LiveAnnouncer';
import { TourNotification } from '@app/shared/state/notification.atom';
import { useRecoilValue } from 'recoil';
import { atom_tourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface VisibleNotification {
	notification: TourNotification;
	id: string;
	startTime: number;
}

interface NotificationToastProps {
	displayDuration?: number;
	staggerDelay?: number;
}

export const NotificationToast = React.memo(function NotificationToast({
	displayDuration = 5000,
	staggerDelay = 1000,
}: NotificationToastProps) {
	const { activeMessage, dismissMessage, markAsSeen } = useNotificationTrigger();
	const [visibleNotifications, setVisibleNotifications] = useState<VisibleNotification[]>([]);
	const processedIdsRef = useRef<Set<string>>(new Set());
	const tourState = useRecoilValue(atom_tourState);
	const isDesktop = useRecoilValue(atom_isDesktop);


	// Get tour color or fallback
	const tourColor = tourState?.config?.mainColor || '#E65100';

	// Keep ref to always have latest dismissMessage in timeouts
	const dismissRef = useRef(dismissMessage);
	dismissRef.current = dismissMessage;

	// Remove a notification - AnimatePresence handles the exit animation
	const removeNotification = useCallback((id: string) => {
		setVisibleNotifications(prev => prev.filter(n => n.id !== id));
	}, []);

	// Handle manual dismiss
	const handleDismiss = useCallback((id: string) => {
		// Mark as seen when user manually closes (same as skipped)
		markAsSeen(id, 'seen');
		removeNotification(id);
		dismissMessage();
	}, [removeNotification, dismissMessage, markAsSeen]);

	// Track scheduled count - increments immediately when we schedule, not when visible
	const scheduledCountRef = useRef(0);
	// Track pending timeouts by notification ID - so cleanup doesn't kill other notifications
	const pendingTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

	// Reset refs when there's no active message and no visible notifications
	// This allows notifications to be shown again on subsequent triggers
	useEffect(() => {
		if (!activeMessage && visibleNotifications.length === 0) {
			processedIdsRef.current.clear();
			scheduledCountRef.current = 0;
			// Clear any pending timeouts
			pendingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
			pendingTimeoutsRef.current.clear();
		}
	}, [activeMessage, visibleNotifications.length]);

	// Add new notification to visible stack
	useEffect(() => {
		if (!activeMessage) return;
		if (processedIdsRef.current.has(activeMessage.id)) {
			return;
		}

		processedIdsRef.current.add(activeMessage.id);

		// Check if we need to stagger based on how many we've already scheduled
		const delay = scheduledCountRef.current > 0 ? staggerDelay : 0;
		scheduledCountRef.current++;
		// Capture the message for the closure
		const messageToShow = activeMessage;

		const timeoutId = setTimeout(() => {
			// Remove from pending
			pendingTimeoutsRef.current.delete(messageToShow.id);

			// Add to visible stack with start time for progress bar
			setVisibleNotifications(prev => [
				{
					notification: messageToShow,
					id: messageToShow.id,
					startTime: Date.now(),
				},
				...prev,
			]);

			// Announce for screen readers
			// announce(`${messageToShow.title}: ${messageToShow.content}`, 'polite');

			// Mark as seen (send 'seen' action to backend)
			markAsSeen(messageToShow.id, 'seen');

			// Auto-remove after displayDuration
			setTimeout(() => {
				removeNotification(messageToShow.id);
			}, displayDuration);

			// Always call dismiss to move to next in queue
			// dismissMessage reads queue from Recoil snapshot, so it has fresh state
			dismissRef.current();
		}, delay);

		// Store timeout by ID
		pendingTimeoutsRef.current.set(messageToShow.id, timeoutId);

		// Don't clear timeout on cleanup - let it complete
		// We track by ID so re-renders don't kill other notifications
	}, [activeMessage, displayDuration, staggerDelay, markAsSeen, removeNotification]);

	if (visibleNotifications.length === 0) return null;

	return (
		<Box
			role="alert"
			aria-live="polite"
			sx={{
				position: 'fixed',
				top: 16,
				left: isDesktop ? '85%' : '50%',
				transform: 'translateX(-50%)',
				zIndex: Z_INDEX.toast,
				maxWidth: 'calc(100% - 32px)',
				width: isDesktop ? '25vw' : '90vw',
				minWidth: 300,
				display: 'flex',
				flexDirection: 'column',
				gap: 1,
			}}
		>
			<AnimatePresence mode="popLayout">
				{visibleNotifications.map((item) => (
					<motion.div
						key={item.id}
						layout
						initial={{ opacity: 0, y: -40, scale: 0.9 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: -30, scale: 0.95 }}
						transition={{
							type: 'spring',
							stiffness: 200,
							damping: 25,
							layout: { type: 'spring', stiffness: 200, damping: 25 }
						} as Transition}
						style={{
							backgroundColor: '#fff',
							borderRadius: '12px',
							boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
							border: '1px solid #e0e0e0',
							overflow: 'hidden',
						}}
					>
						{/* Content area */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 1.5,
								padding: '12px 16px',
							}}
						>
							{/* Icon */}
							<Box
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: tourColor,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0,
									marginTop: '2px',
								}}
							>
								{item.notification.icon ? (
									<img
										src={item.notification.icon}
										alt=""
										style={{ width: 14, height: 14 }}
									/>
								) : (
									<InfoIcon sx={{ color: '#fff', fontSize: 16 }} />
								)}
							</Box>

							{/* Content */}
							<Box sx={{ flex: 1, minWidth: 0 }}>
								{item.notification.title && (
									<Typography
										variant="subtitle2"
										sx={{
											fontWeight: 600,
											color: '#333',
											marginBottom: 0.25,
										}}
									>
										{item.notification.title}
									</Typography>
								)}
								<Typography
									component="div"
									variant="body2"
									sx={{
										color: '#555',
										lineHeight: 1.4,
										'& p': { margin: 0 },
										'& strong': { fontWeight: 600 },
									}}
									dangerouslySetInnerHTML={{ __html: item.notification.content }}
								/>
							</Box>

							{/* Close button */}
							<IconButton
								onClick={() => handleDismiss(item.id)}
								size="small"
								aria-label="Close notification"
								sx={{
									padding: 0.5,
									marginTop: '-4px',
									marginRight: '-8px',
									color: '#999',
									'&:hover': {
										color: '#666',
										backgroundColor: 'transparent',
									},
								}}
							>
								<CloseIcon sx={{ fontSize: 20 }} />
							</IconButton>
						</Box>

						{/* Progress bar */}
						<Box
							sx={{
								height: 4,
								backgroundColor: `${tourColor}30`, // 30% opacity for background
								position: 'relative',
							}}
						>
							<Box
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									height: '100%',
									backgroundColor: tourColor,
									animation: `progressCountdown ${displayDuration}ms linear forwards`,
									'@keyframes progressCountdown': {
										'0%': {
											width: '100%',
										},
										'100%': {
											width: '0%',
										},
									},
								}}
							/>
						</Box>
					</motion.div>
				))}
			</AnimatePresence>
		</Box>
	);
});
